import React, { useState } from "react";
import {
  AddressFields,
  ClientPhoneField,
  EmailField,
  NameField,
  PoliciesField,
  TeamEmailField,
  WebsiteField,
} from "../CreateEditClient/CreateEditClientForm";
import {
  ADDRESS_FIELDS,
  EditPencil,
  IncompleteBadge,
  RichTextView,
  SectionEditModal,
  useClientSectionForm,
} from "../ClientSections";
import { createPortal } from "react-dom";
import BaseButton from "../../../components/ui/BaseButton";
import BaseLoader from "../../../components/ui/BaseLoader";
import BaseWrapper from "../../../components/ui/BaseWrapper";
import InfoCard from "../../../components/ui/InfoCard";
import FileChip from "../../../components/ui/FileChip";
import useAuth from "../../../hooks/useAuth";
import { ModuleType } from "../../../utils/enum";
import { formatPhoneNumber, getDefaultEntity, isSelfSignup } from "../../../utils/helpers";
import GenerateSignupModal from "../CreateEditClient/components/GenerateSignupModal";
import { parentValidationSchema } from "../CreateEditClient";

const SECTIONS = {
  logo: { title: "Company Logo", fields: ["clientLogo"] },
  information: {
    title: "Company Information",
    fields: ["clientName", "email", "phone", "teamEmail", "website"],
    Fields: ({ ctx }) => (
      <>
        <NameField ctx={ctx} />
        <EmailField ctx={ctx} />
        <ClientPhoneField ctx={ctx} />
        <TeamEmailField ctx={ctx} />
        <WebsiteField ctx={ctx} />
      </>
    ),
  },
  address: { title: "Address", fields: ADDRESS_FIELDS, Fields: AddressFields },
  policies: { title: "Company's Policies", fields: ["policies"], Fields: PoliciesField },
};

const ParentCustomerProfile = ({ clientId, actionsContainer = null }) => {
  const { getPermissionForModule } = useAuth();
  const { hasUpdateAccess } = getPermissionForModule(ModuleType.CLIENT_CUSTOMER);
  const [isGenerateSignupOpen, setIsGenerateSignupOpen] = useState(false);
  const [logoFailed, setLogoFailed] = useState(false);

  const sectionForm = useClientSectionForm({
    clientId,
    sections: SECTIONS,
    schema: parentValidationSchema,
  });
  const {
    client: customer,
    formik,
    isLoading,
    editingSection,
    openSection,
    incompleteSections,
  } = sectionForm;

  if (isLoading) return <BaseLoader isLoading />;
  if (!customer) return null;

  const pencil = (key) => hasUpdateAccess && <EditPencil onClick={() => openSection(key)} />;

  const defaultEntity = getDefaultEntity(customer);
  const badge = (key) => incompleteSections.has(key) && <IncompleteBadge />;

  const logoUrl = customer.clientLogo?.url;
  // A newly picked file previews from the browser; a saved one from its stored link.
  const pickedLogo = formik.values.clientLogo;
  const logoPreview =
    pickedLogo instanceof File ? URL.createObjectURL(pickedLogo) : pickedLogo?.url || null;

  // Only a customer without an account yet can be sent a signup link.
  const headerActions = hasUpdateAccess && !customer.ownerId && (
    <BaseButton
      variant="contained"
      color="primary"
      onClick={() => setIsGenerateSignupOpen(true)}
      className="py-[5px]"
    >
      Generate Signup
    </BaseButton>
  );

  return (
    <BaseWrapper className="pb-6">
      {actionsContainer && headerActions && createPortal(headerActions, actionsContainer)}

      <InfoCard title="Company Logo" badge={badge("logo")} actions={pencil("logo")}>
        <div className="py-4">
          <div className="h-40 flex flex-col items-center justify-center gap-2 w-full border-2 border-dashed border-gray-300 rounded-md p-4">
            {logoUrl && !logoFailed ? (
              <img
                src={logoUrl}
                alt="Company Logo"
                className="max-h-32 object-contain rounded"
                onError={() => setLogoFailed(true)}
              />
            ) : (
              <>
                <i className="fa-regular fa-image text-gray-400 text-2xl"></i>
                <span className="text-sm text-gray-500">No logo uploaded</span>
              </>
            )}
          </div>
        </div>
      </InfoCard>

      <InfoCard
        title="Company Information"
        badge={badge("information")}
        actions={pencil("information")}
        items={[
          ["Company Name", customer.clientName],
          ["Email", customer.email],
          ["Phone", formatPhoneNumber(customer.phoneNumber)],
          ["Team Email", customer.teamEmail],
          ["Website", customer.website],
        ]}
      />

      <InfoCard
        title="Address"
        badge={badge("address")}
        actions={pencil("address")}
        items={[
          ["Address", customer.address?.addressLine1],
          ["City", customer.address?.city],
          ["State", customer.address?.state],
          ["Zip / Post Code", customer.address?.zipCode],
          ["Country", customer.address?.country],
        ]}
      />

      <InfoCard title="Company's Policies" badge={badge("policies")} actions={pencil("policies")}>
        <RichTextView value={customer.policies} className="pt-4 pb-2" />
      </InfoCard>

      {isSelfSignup(customer) && (
        <>
          <InfoCard
            title="Registration"
            items={[
              ["Registration Number", defaultEntity?.registrationNumber],
              [
                "Registration Certificate",
                <FileChip file={defaultEntity?.registrationCertificate} />,
              ],
              ["VAT/GST Registration", defaultEntity?.taxRegistrationNumber],
            ]}
          />
          <InfoCard
            title="Accounts"
            items={[
              ["Accounts Person", defaultEntity?.clientAccount?.accountsPerson],
              ["Accounts Email", defaultEntity?.clientAccount?.email],
              ["Accounts Phone", formatPhoneNumber(defaultEntity?.clientAccount?.phone)],
            ]}
          />
        </>
      )}

      <SectionEditModal
        sectionForm={sectionForm}
        formProps={{
          recordLabel: "Company",
        }}
      >
        {editingSection === "logo" && (
          // The logo is uploaded into the same dashed frame it is read in.
          <div className="px-4 pt-4">
            <div
              className="h-48 flex flex-col items-center justify-center gap-2 w-full border-2 border-dashed border-gray-300 rounded-md p-4 cursor-pointer bg-white hover:border-primary transition"
              onClick={() => document.getElementById("parentCustomerLogoInput").click()}
            >
              {logoPreview ? (
                <img
                  src={logoPreview}
                  alt="Company Logo"
                  className="max-h-32 object-contain rounded"
                />
              ) : (
                <>
                  <i className="fa-solid fa-paperclip text-primary text-2xl"></i>
                  <span className="text-sm text-gray-500">Click or drop file here</span>
                  <span className="text-xs text-gray-400">Must be a JPG or PNG file</span>
                </>
              )}
              <input
                type="file"
                id="parentCustomerLogoInput"
                accept="image/png, image/jpeg, image/webp"
                className="hidden"
                onChange={(event) => {
                  const picked = event.target.files[0];
                  if (picked) {
                    formik.setFieldValue("clientLogo", picked);
                    setLogoFailed(false);
                  }
                }}
              />
            </div>
          </div>
        )}
      </SectionEditModal>

      {isGenerateSignupOpen && (
        <GenerateSignupModal
          open={isGenerateSignupOpen}
          close={() => setIsGenerateSignupOpen(false)}
          selectedClient={customer}
        />
      )}
    </BaseWrapper>
  );
};

export default ParentCustomerProfile;
